import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Search, X, Calendar, ArrowRight, BookOpen, Network, Building2 } from "lucide-react";
import { entitySlug } from "../components/EntityCard";
import { GeoChipRow, IntensityChipRow } from "../components/TagFilters";
import SEO from "../components/SEO";
import ShareDock from "../components/ShareDock";
import { ContinueInvestigationCTA } from "../components/PremiumLock";
import { GhostTimelineRow } from "../components/GhostCards";
import { trackTimelineView } from "../lib/analytics";

const TIME_RANGES = [
    { key: "all", label: "All time" },
    { key: "5y", label: "Last 5 years", from: 2021 },
    { key: "decade", label: "Past decade (2015+)", from: 2015 },
    { key: "2000s", label: "2000s (2000-2009)", from: 2000, to: 2009 },
    { key: "1990s", label: "1990s (1990-1999)", from: 1990, to: 1999 },
    { key: "pre-1990", label: "Pre-1990", to: 1989 },
];

function decadeOf(year) {
    if (!year) return null;
    return Math.floor(year / 10) * 10;
}

function EventCard({ ev, onSelect, isActive }) {
    return (
        <button
            type="button"
            onClick={onSelect}
            data-testid={`timeline-event-${ev.topic_slug}-${ev.date}`}
            className={`group relative w-full text-left bg-[#111827] border rounded-2xl p-5 transition-all ${isActive ? "border-[#56CCF2] shadow-[0_0_0_3px_rgba(86,204,242,0.15)]" : "border-[#243044] hover:border-[#2F80ED]/60"}`}
        >
            <div className="flex items-start gap-4">
                <div className="flex flex-col items-center pt-1">
                    <div className="w-3 h-3 rounded-full bg-[#2F80ED] ring-4 ring-[#0B1220] group-hover:bg-[#56CCF2] transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-bold">
                            {ev.date}
                        </span>
                        {ev.category && (
                            <span className="text-[10px] uppercase tracking-wider text-[#64748B]">
                                · {ev.category}
                            </span>
                        )}
                    </div>
                    <h3 className="text-base font-semibold text-white leading-snug mb-2 group-hover:text-[#56CCF2] transition-colors">
                        {ev.title}
                    </h3>
                    {ev.description && (
                        <p className="text-sm text-[#94A3B8] leading-relaxed line-clamp-2 mb-3">
                            {ev.description}
                        </p>
                    )}
                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-[#64748B]">
                        <span className="inline-flex items-center gap-1.5">
                            <Network className="w-3 h-3" />
                            via {ev.topic_title}
                        </span>
                        <span>· {ev.source_count} sources</span>
                    </div>
                </div>
            </div>
        </button>
    );
}

function EventDetailPanel({ ev, onClose }) {
    if (!ev) return null;
    return (
        <div className="fixed inset-0 z-50 flex" data-testid="timeline-event-panel">
            <button
                className="flex-1 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
                aria-label="Close panel"
            />
            <aside className="w-full max-w-lg bg-[#0B1220] border-l border-[#243044] overflow-y-auto">
                <div className="sticky top-0 bg-[#0B1220]/95 backdrop-blur-xl border-b border-[#243044] px-6 py-4 flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium">
                        Timeline event
                    </span>
                    <button onClick={onClose} className="text-[#64748B] hover:text-white" data-testid="timeline-panel-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="px-6 py-6 space-y-6">
                    <div>
                        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#56CCF2] mb-3">
                            {ev.date}
                        </div>
                        <h2 className="text-2xl font-bold text-white tracking-tight leading-snug mb-4">
                            {ev.title}
                        </h2>
                        {ev.description && (
                            <p className="text-[15px] leading-[1.7] text-[#CBD5E1]">{ev.description}</p>
                        )}
                    </div>

                    <div className="bg-[#111827] border border-[#243044] rounded-xl p-5">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
                            From the investigation
                        </div>
                        <Link
                            to={`/topic/${ev.topic_slug}`}
                            data-testid="timeline-panel-topic-link"
                            className="group block"
                        >
                            <p className="text-base font-semibold text-white group-hover:text-[#56CCF2] mb-1 transition-colors">
                                {ev.topic_title}
                            </p>
                            <p className="text-xs text-[#64748B] inline-flex items-center gap-2">
                                {ev.category}
                                <ArrowRight className="w-3 h-3" />
                                Open full investigation
                            </p>
                        </Link>
                    </div>

                    {ev.entities && ev.entities.length > 0 && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">
                                Entities involved
                                <span className="ml-2 text-[#56CCF2]">· via shared investigation</span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {ev.entities.map((e, i) => {
                                    const slug = entitySlug(e.name);
                                    const inner = (
                                        <>
                                            <Building2 className="w-3 h-3 text-[#56CCF2]" />
                                            {e.name}
                                        </>
                                    );
                                    const cls = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#111827] border border-[#243044] text-[11px] text-[#CBD5E1] hover:border-[#2F80ED] hover:text-white transition-colors";
                                    return slug ? (
                                        <Link key={i} to={`/entity/${slug}`} className={cls}>{inner}</Link>
                                    ) : (
                                        <span key={i} className={cls}>{inner}</span>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <div className="pt-2 border-t border-[#243044] text-xs text-[#64748B] inline-flex items-center gap-2">
                        <BookOpen className="w-3 h-3" />
                        {ev.source_count} sources documented in the parent investigation
                    </div>
                </div>
            </aside>
        </div>
    );
}

export default function MasterTimelinePage() {
    const [events, setEvents] = useState([]);
    const [gate, setGate] = useState({ is_member: true });
    const [facets, setFacets] = useState({ total_events: 0, year_min: null, year_max: null, categories: [], topics: [], entities: [] });
    const [loading, setLoading] = useState(true);

    const [q, setQ] = useState("");
    const [category, setCategory] = useState("");
    const [topic, setTopic] = useState("");
    const [entity, setEntity] = useState("");
    const [geo, setGeo] = useState("");
    const [intensityFilter, setIntensityFilter] = useState("");
    const [rangeKey, setRangeKey] = useState("all");
    const [activeEvent, setActiveEvent] = useState(null);

    const decadeRefs = useRef({});

    const [globalFilters, setGlobalFilters] = useState({ geographies: [], intensities: [] });

    useEffect(() => {
        api.get("/timeline/facets").then((r) => setFacets(r.data)).catch(() => {});
        api.get("/filters").then((r) => setGlobalFilters(r.data || { geographies: [], intensities: [] })).catch(() => {});
        trackTimelineView();
    }, []);

    useEffect(() => {
        setLoading(true);
        const range = TIME_RANGES.find((r) => r.key === rangeKey) || TIME_RANGES[0];
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        if (topic) params.set("topic", topic);
        if (entity) params.set("entity", entity);
        if (geo) params.set("geography", geo);
        if (intensityFilter) params.set("intensity", intensityFilter);
        if (range.from) params.set("from_year", String(range.from));
        if (range.to) params.set("to_year", String(range.to));
        api.get(`/timeline/events${params.toString() ? "?" + params.toString() : ""}`)
            .then((r) => {
                // Backend now returns {events, _gate}. Fall back to legacy array if needed.
                const payload = r.data || {};
                if (Array.isArray(payload)) {
                    setEvents(payload);
                    setGate({ is_member: true });
                } else {
                    setEvents(payload.events || []);
                    setGate(payload._gate || { is_member: true });
                }
            })
            .catch(() => { setEvents([]); setGate({ is_member: true }); })
            .finally(() => setLoading(false));
    }, [category, topic, entity, geo, intensityFilter, rangeKey]);

    // Escape closes the event detail panel
    useEffect(() => {
        if (!activeEvent) return;
        const onKey = (e) => { if (e.key === "Escape") setActiveEvent(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [activeEvent]);

    const filteredEvents = useMemo(() => {
        if (!q.trim()) return events;
        const ql = q.trim().toLowerCase();
        return events.filter((e) => {
            const hay = [e.title, e.description, e.topic_title, e.category, ...(e.entities || []).map((x) => x.name)].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(ql);
        });
    }, [events, q]);

    // Group by decade for the sticky rail and empty-decade scaffolding.
    const { decadeGroups, decadeKeys } = useMemo(() => {
        const groups = {};
        for (const ev of filteredEvents) {
            const dec = decadeOf(ev.year);
            const key = dec ?? "Undated";
            (groups[key] ||= []).push(ev);
        }
        const keys = Object.keys(groups).sort((a, b) => {
            // Numeric decades sorted desc; "Undated" goes last
            if (a === "Undated") return 1;
            if (b === "Undated") return -1;
            return Number(b) - Number(a);
        });
        return { decadeGroups: groups, decadeKeys: keys };
    }, [filteredEvents]);

    // Scaffolded list of all decades within the chosen range — including empty ones.
    const allDecadesInRange = useMemo(() => {
        const present = decadeKeys.filter((k) => k !== "Undated").map(Number);
        if (present.length === 0) return [];
        const min = Math.min(...present);
        const max = Math.max(...present);
        const out = [];
        for (let d = max; d >= min; d -= 10) out.push(d);
        if (decadeKeys.includes("Undated")) out.push("Undated");
        return out;
    }, [decadeKeys]);

    const scrollToDecade = (dec) => {
        const el = decadeRefs.current[dec];
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const clearAll = () => { setQ(""); setCategory(""); setTopic(""); setEntity(""); setGeo(""); setIntensityFilter(""); setRangeKey("all"); };
    const anyFilter = q || category || topic || entity || geo || intensityFilter || rangeKey !== "all";

    return (
        <div className="min-h-screen bg-[#0B1220]">
            <SEO
                title="Master Timeline"
                description="Every documented event across the library, sequenced newest first — institutional change, legislation, surveillance infrastructure, food systems, finance, technology. Click any event to open the underlying investigation."
                canonicalPath="/timeline"
            />
            <ShareDock
                title="Master Investigative Timeline"
                description="Every documented event across the library, sequenced newest first."
                type="timeline"
                id="master"
            />
            <Header />

            {/* Hero */}
            <section className="relative overflow-hidden border-b border-[#243044]">
                <div className="absolute inset-0 bg-grid opacity-30" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/40 via-[#0B1220]/85 to-[#0B1220]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 sm:pt-24 sm:pb-14">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#111827] border border-[#243044] mb-8">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#56CCF2] animate-pulse" />
                            <span className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
                                Master investigative timeline
                            </span>
                        </div>
                        <h1 data-testid="timeline-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05] mb-6">
                            How we got here.
                        </h1>
                        <p className="text-lg text-[#94A3B8] leading-relaxed max-w-2xl">
                            Every documented event from every investigation in the library, sequenced newest first. Click any event to open the investigation it belongs to. Built from the topic record — no algorithmic guesses, only documented connections.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8 text-xs text-[#64748B]">
                            <span data-testid="timeline-total"><span className="text-white font-bold">{facets.total_events}</span> documented events</span>
                            {facets.year_min && facets.year_max && (
                                <span>· <span className="text-white font-bold">{facets.year_min}–{facets.year_max}</span></span>
                            )}
                            <span>· <span className="text-white font-bold">{facets.topics.length}</span> investigations</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filters */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <div className="bg-[#111827] border border-[#243044] rounded-2xl p-5 space-y-4">
                    <div className="flex items-center gap-3 bg-[#0B1220] border border-[#243044] rounded-xl px-4 py-3 focus-within:border-[#2F80ED]">
                        <Search className="w-4 h-4 text-[#64748B] shrink-0" />
                        <input
                            data-testid="timeline-search-input"
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search events — by event title, entity, organisation, legislation…"
                            className="bg-transparent border-0 outline-none text-sm text-white placeholder-[#64748B] flex-1"
                        />
                        {q && <button onClick={() => setQ("")} className="text-[#64748B] hover:text-white"><X className="w-4 h-4" /></button>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Time range</label>
                            <select
                                data-testid="timeline-range"
                                value={rangeKey}
                                onChange={(e) => setRangeKey(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                {TIME_RANGES.map((r) => (
                                    <option key={r.key} value={r.key}>{r.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Category</label>
                            <select
                                data-testid="timeline-filter-category"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                <option value="">All categories</option>
                                {facets.categories.map((c) => (
                                    <option key={c.value} value={c.value}>{c.value} ({c.count})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Investigation</label>
                            <select
                                data-testid="timeline-filter-topic"
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                <option value="">All investigations</option>
                                {facets.topics.map((t) => (
                                    <option key={t.slug} value={t.slug}>{t.title} ({t.event_count})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-1.5 block">Entity</label>
                            <select
                                data-testid="timeline-filter-entity"
                                value={entity}
                                onChange={(e) => setEntity(e.target.value)}
                                className="w-full bg-[#0B1220] border border-[#243044] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#2F80ED]"
                            >
                                <option value="">All entities</option>
                                {facets.entities.map((e) => (
                                    <option key={e.value} value={e.value}>{e.value}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {(globalFilters.geographies?.length > 0 || globalFilters.intensities?.length > 0) && (
                        <div className="pt-2 space-y-3 border-t border-[#243044]/60">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Region</p>
                                <GeoChipRow value={geo} options={globalFilters.geographies} onChange={setGeo} dataTestId="timeline-geo" />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Intensity</p>
                                <IntensityChipRow value={intensityFilter} options={globalFilters.intensities} onChange={setIntensityFilter} dataTestId="timeline-intensity" />
                            </div>
                        </div>
                    )}

                    {anyFilter && (
                        <div className="flex items-center justify-between pt-1">
                            <div data-testid="timeline-result-count" className="text-xs text-[#64748B]">
                                {loading ? "Loading…" : `${filteredEvents.length} event${filteredEvents.length === 1 ? "" : "s"}`}
                            </div>
                            <button data-testid="timeline-clear-filters" onClick={clearAll} className="text-xs text-[#56CCF2] hover:text-white">Clear all</button>
                        </div>
                    )}
                </div>
            </section>

            {/* Body: decade rail + events */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {loading ? (
                    <div className="text-center text-sm text-[#64748B] py-20">Loading the timeline…</div>
                ) : filteredEvents.length === 0 ? (
                    <div className="text-center py-20">
                        <p className="text-base text-[#CBD5E1] mb-2">No events match your filters.</p>
                        <button onClick={clearAll} className="text-sm text-[#56CCF2] hover:text-white">Clear filters</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-10">
                        {/* Sticky decade rail */}
                        <aside className="hidden lg:block">
                            <div className="sticky top-24 space-y-1">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] mb-3">Jump to</div>
                                {allDecadesInRange.map((dec) => {
                                    const count = decadeGroups[dec]?.length || 0;
                                    return (
                                        <button
                                            key={dec}
                                            onClick={() => scrollToDecade(dec)}
                                            data-testid={`timeline-decade-jump-${dec}`}
                                            className={`block w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${count > 0 ? "text-[#CBD5E1] hover:bg-[#111827] hover:text-white" : "text-[#64748B] cursor-default"}`}
                                        >
                                            <span className="font-mono">{dec === "Undated" ? "Undated" : `${dec}s`}</span>
                                            <span className="text-[10px] text-[#64748B] ml-2">{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </aside>

                        {/* Decades + events */}
                        <div className="space-y-10">
                            {allDecadesInRange.map((dec) => {
                                const evs = decadeGroups[dec] || [];
                                return (
                                    <div key={dec} ref={(el) => { decadeRefs.current[dec] = el; }} className="scroll-mt-24">
                                        <div className="flex items-baseline gap-4 mb-5">
                                            <h2 className="text-3xl font-bold text-white tracking-tight">
                                                {dec === "Undated" ? "Undated" : `${dec}s`}
                                            </h2>
                                            <span className="text-xs text-[#64748B] uppercase tracking-wider">
                                                {evs.length} event{evs.length === 1 ? "" : "s"}
                                            </span>
                                        </div>
                                        {evs.length === 0 ? (
                                            <div data-testid={`timeline-decade-empty-${dec}`} className="bg-[#111827] border border-dashed border-[#243044] rounded-2xl p-5 text-sm text-[#64748B] italic">
                                                Few events catalogued from this decade — coverage will deepen as new investigations are added.
                                            </div>
                                        ) : (
                                            <div className="relative space-y-3">
                                                <div className="absolute left-1.5 top-3 bottom-3 w-px bg-[#243044]" />
                                                {evs.map((ev, i) => (
                                                    <EventCard
                                                        key={`${ev.topic_slug}-${ev.date}-${i}`}
                                                        ev={ev}
                                                        onSelect={() => setActiveEvent(ev)}
                                                        isActive={activeEvent && activeEvent.topic_slug === ev.topic_slug && activeEvent.date === ev.date && activeEvent.title === ev.title}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </section>

            <EventDetailPanel ev={activeEvent} onClose={() => setActiveEvent(null)} />

            {/* Premium continuation — ghost rows + CTA for non-members */}
            {!gate.is_member && (gate.ghosts?.timeline?.length || 0) > 0 && (
                <section data-testid="timeline-premium" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    <div className="bg-[#0F1828] border border-dashed border-[#243044] rounded-2xl p-6">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#64748B] mb-4">
                            {gate.ghosts.timeline.length} earlier event{gate.ghosts.timeline.length === 1 ? "" : "s"} in the members archive
                        </div>
                        <div className="space-y-3">
                            {gate.ghosts.timeline.slice(0, 12).map((g, i) => (
                                <GhostTimelineRow key={i} item={g} />
                            ))}
                        </div>
                    </div>
                    <div className="mt-8">
                        <ContinueInvestigationCTA
                            label="Open the full timeline"
                            subline="Every documented event across every investigation — including pre-2010 institutional history, regulatory archive, and the deep-time evidence chain — reserved for members."
                            testId="timeline-premium-cta"
                        />
                    </div>
                </section>
            )}

            <Footer />
        </div>
    );
}
